import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaIdCard } from 'react-icons/fa';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import Page from 'Core/components/Page';
import Card from 'Core/components/FormElements/Card';
import { PrimaryButton } from 'Core/components/Button';
import InfoBox from 'Core/components/InfoBox';
import Loader from 'Core/components/Loader';
import { AlertError } from 'Core/components/Alert';
import useExistingAccountSubmit from 'Modules/patientfi/hooks/useExistingAccountSubmit';
import { formatAccountDate, isReapplyOpen } from 'Modules/patientfi/utils/accountDate';

export default function FinancingDenied({ application = {} }) {
  const { t } = useTranslation();
  document.title = t('patientfi.existingAccount.pageTitle', 'Payment Portal - PatientFi Account');
  useScrollTop();

  const { payload: { existing_account: existingAccount } = {} } = application;
  const { reapply_date: reapplyDate, matched_by: matchedBy } = existingAccount || {};

  const canReapply = isReapplyOpen(reapplyDate);
  const reapplyDateLabel = formatAccountDate(reapplyDate);
  const { submit, submitError, busy } = useExistingAccountSubmit();

  const matchedByLabel =
    matchedBy === 'EMAIL'
      ? t(
          'patientfi.existingAccount.matchedBy.email',
          'We found this account using your email address.'
        )
      : t(
          'patientfi.existingAccount.matchedBy.phone',
          'We found this account using your phone number.'
        );

  return (
    <Page
      title={t('page.financing.header')}
      withStepProgress={false}
      stickyFooter
      branding="patientfi"
    >
      {busy && (
        <Loader
          message={t(
            'patientfi.existingAccount.loading',
            'Checking your PatientFi account. This can take a moment.'
          )}
        />
      )}

      <div className="max-w-4xl mx-auto mt-6">
        <Card className="!p-0 overflow-hidden" cardPadding="none">
          <div className="bg-gradient-to-r from-sky-600 to-teal-500 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <FaIdCard className="text-white text-xl" />
              </div>
              <div>
                <h1
                  className="text-xl font-semibold text-white"
                  data-testid="title-existing-denied"
                >
                  {t('patientfi.existingAccount.title', 'We found your PatientFi account')}
                </h1>
                <p className="text-sky-100 text-sm">{matchedByLabel}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <p className="text-gray-600 mb-6" data-testid="existing-denied-description">
              {canReapply
                ? t(
                    'patientfi.existingAccount.denied.canReapply',
                    'Your previous PatientFi application was not approved. The good news is that you are now eligible to reapply. If you are approved, you can use your PatientFi credit for this purchase.'
                  )
                : t(
                    'patientfi.existingAccount.denied.waitToReapply',
                    'Your previous PatientFi application was not approved recently, so a new application is not possible just yet. You can reapply on or after {{date}}. In the meantime, please choose another payment option or contact us if you have any questions.',
                    { date: reapplyDateLabel }
                  )}
            </p>

            {!canReapply && (
              <InfoBox type="warningLite" showBorder>
                <span className="font-bold mr-2">
                  {t('patientfi.existingAccount.denied.reapplyOn', 'Eligible to reapply on')}:
                </span>
                <span data-testid="existing-denied-reapply-date">{reapplyDateLabel}</span>
              </InfoBox>
            )}

            {submitError && (
              <AlertError className="mt-4" data-testid="existing-denied-error">
                {submitError}
              </AlertError>
            )}

            {canReapply && (
              <PrimaryButton
                className="mt-6 px-12"
                data-testid="btn-existing-denied-reapply"
                disabled={busy}
                onClick={() => submit(false)}
              >
                {t('common.reapply')}
              </PrimaryButton>
            )}
          </div>
        </Card>
      </div>
    </Page>
  );
}
